import { lazyWithChunkRecovery } from "./chunkLoadRecovery";

export const routeLoaders = {
  profile: lazyWithChunkRecovery(() => import("../components/Tabs/Profile")),
  estimates: lazyWithChunkRecovery(() => import("../components/Tabs/Estimates")),
  results: lazyWithChunkRecovery(() => import("../components/Tabs/MyResults")),
  films: lazyWithChunkRecovery(() => import("../components/Tabs/Films")),
  collections: lazyWithChunkRecovery(() => import("../components/Tabs/CuratedLists")),
  game: lazyWithChunkRecovery(() => import("../components/Tabs/GameModes")),
  friends: lazyWithChunkRecovery(() => import("../components/Tabs/Friends")),
  achievements: lazyWithChunkRecovery(() => import("../components/Tabs/Achievements")),
  notifications: lazyWithChunkRecovery(() => import("../components/Tabs/NotificationsLog")),
  filmPage: lazyWithChunkRecovery(() => import("../components/InsertElements/FilmPage"))
};

const preloadedRoutes = new Set<keyof typeof routeLoaders>();

export const preloadRoute = (routeName: unknown) => {
  if (typeof routeName !== "string" || !Object.prototype.hasOwnProperty.call(routeLoaders, routeName)) return;
  const key = routeName as keyof typeof routeLoaders;
  const loader = routeLoaders[key];
  if (!loader || preloadedRoutes.has(key)) return;
  preloadedRoutes.add(key);
  void loader().catch(() => preloadedRoutes.delete(key));
};

const canPreloadRoutes = () => {
  const connection = typeof navigator !== "undefined"
    ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
    : null;
  const effectiveType = String(connection?.effectiveType || "").toLowerCase();
  return !connection?.saveData && !effectiveType.includes("2g");
};

const getIntentRoute = (target: EventTarget | null) => {
  const anchor = target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
  if (!anchor) return null;

  try {
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    return /\/film-page\/(?:movie|tv)\//.test(url.pathname) ? "filmPage" : null;
  } catch {
    return null;
  }
};

/**
 * Preload large route chunks only after the user shows navigation intent.
 * This replaces the old unconditional idle preload, which downloaded the film
 * page even for sessions that never opened a film.
 */
export const installRouteIntentPreloading = (
  root = typeof document !== "undefined" ? document : null
) => {
  if (!root?.addEventListener || !canPreloadRoutes()) return () => {};

  const handleIntent = (event: Event) => {
    const routeName = getIntentRoute(event.target);
    if (routeName) preloadRoute(routeName);
  };

  root.addEventListener("pointerover", handleIntent, { passive: true });
  root.addEventListener("focusin", handleIntent);
  root.addEventListener("touchstart", handleIntent, { passive: true });

  return () => {
    root.removeEventListener("pointerover", handleIntent);
    root.removeEventListener("focusin", handleIntent);
    root.removeEventListener("touchstart", handleIntent);
  };
};
